import { featureFlags } from "@kaggle/sdk";
import { getLogger } from "../../../../../api/getServices";
import { CellType } from "../../../../types/CellType";
import { EditorStatus } from "../../../../types/EditorStatus";
import { VersionsLoadStatus } from "../../../../types/VersionsLoadStatus";
import { EditorActionKey, VersionActionKey } from "../../../actions/ActionKeys";
import { EditorAction } from "../../../actions/editor";
import { JupyterAction, JupyterActionKey, SetCellStateAction } from "../../../actions/jupyter";
import { VersionsAction } from "../../../actions/versions";
import { JupyterStatus  } from "../../../types";

/**
 * Manages the status of the sessionless Jupyter editor.
 *
 * Note this is different than the status of the worker which runs the
 * editor. For example, when the app first loads, the URL to be used by
 * the iframe which renders the notebook is not yet known, thus the editor is QUEUED.
 */
export const status = (
  state: EditorStatus = EditorStatus.QUEUED,
  action: EditorAction | JupyterAction
): EditorStatus => {
  // Cannot exit FATAL state. Page refresh required.
  if (state === EditorStatus.FATAL) {
    return EditorStatus.FATAL;
  }

  switch (action.type) {
    case JupyterActionKey.SET_JUPYTER_STATUS:
      // Transitions out of ERROR handled by explicitly setting editor state.
      if (state === EditorStatus.ERROR) {
        return EditorStatus.ERROR;
      }
      switch (action.payload.status) {
        case JupyterStatus.RUNNING:
          return EditorStatus.STARTING;
        case JupyterStatus.CONNECTED:
          return EditorStatus.RUNNING;
        case JupyterStatus.DISCONNECTED:
          return EditorStatus.STOPPED;
        default:
          return state;
      }
    case JupyterActionKey.SET_JUPYTER_STATE:
      // Transitions out of ERROR handled by explicitly setting editor state.
      if (state === EditorStatus.ERROR) {
        return EditorStatus.ERROR;
      }
      switch (action.payload.jupyter.status) {
        case JupyterStatus.RUNNING:
          // Both ERROR (handled above) and STOPPED indicate that there's no backend
          // session being created, so we should never transition to STARTING.
          if (
            featureFlags.kernelsPreventStoppedToStartingTransition &&
            state === EditorStatus.STOPPED
          ) {
            getLogger().report("kernels", "notebook_error", {
              errorCode: "invalid_state_transition",
              message: "Attempted to transition from STOPPED to STARTING",
            });
            return EditorStatus.STOPPED;
          }
          return EditorStatus.STARTING;
        case JupyterStatus.CONNECTED:
          return EditorStatus.RUNNING;
        case JupyterStatus.DISCONNECTED:
          return EditorStatus.STOPPED;
        default:
          return state;
      }
    case EditorActionKey.SET_EDITOR_STATUS:
      // Transition to RUNNING only possible from STARTING.
      if (
        action.payload.status === EditorStatus.RUNNING &&
        state !== EditorStatus.STARTING
      ) {
        if (state !== EditorStatus.RUNNING) {
          getLogger().report("kernels", "notebook_error", {
            errorCode: "invalid_state_transition",
            message: `Attempted to transition from ${state} to RUNNING`,
          });
        }
        return state;
      }
      return action.payload.status;
    case EditorActionKey.EDITOR_ERROR:
      // TODO(cochamberlain): EDITOR_ERROR is also the action used to clear the editor error. We should fix that.
      if (action.payload.error !== null) {
        return action.payload.status;
      }
      return state;
    default:
      return state;
  }
};

export const versionsLoadStatus = (
  state: VersionsLoadStatus = "loading",
  action: VersionsAction
): VersionsLoadStatus => {
  switch (action.type) {
    case VersionActionKey.FETCH_VERSIONS_STARTED:
      return "loading";
    case VersionActionKey.FETCH_VERSIONS_SUCCESS:
      return "loaded";
    case VersionActionKey.FETCH_VERSIONS_FAILURE:
      return "failed";
    default:
      return state;
  }
};

export const selectedCellType = (
  state: CellType = "raw",
  action: SetCellStateAction
): CellType => {
  switch (action.type) {
    case JupyterActionKey.SET_CELL_STATE:
      // Returns the type of all selected cells, if consistent, otherwise 'raw'.
      const selected = action.payload.state.filter(cell => cell.isSelected);
      if (selected.length === 0) {
        return "raw";
      }
      return selected.
              map(cell => cell.cellType).
              reduce((current, next) => current === next ? current : "raw");
    default:
      return state;
  }
};

export const selectedCellHidden = (
  state: boolean | null = null,
  action: SetCellStateAction
): boolean | null => {
  switch (action.type) {
    case JupyterActionKey.SET_CELL_STATE:
      // Returns the isHidden state of all selected cells, if consistent, otherwise null.
      const selected = action.payload.state.filter(cell => cell.isSelected);
      if (selected.length === 0) {
        return null;
      }
      return selected.
              map(cell => cell.isHidden as (boolean | null)).
              reduce((current, next): boolean | null => current === next ? current : null);
    default:
      return state;
  }
};

export const selectedCellOutputHidden = (
  state: boolean | null = null,
  action: SetCellStateAction
): boolean | null => {
  switch (action.type) {
    case JupyterActionKey.SET_CELL_STATE:
      // Returns the isOutputHidden state of all selected cells, if consistent, otherwise null.
      const selected = action.payload.state.filter(cell => cell.isSelected);
      if (selected.length === 0) {
        return null;
      }
      return selected.
              map(cell => cell.isOutputHidden as (boolean | null)).
              reduce((current, next): boolean | null => current === next ? current : null);
    default:
      return state;
  }
};
